import {
  bfMetricDataConverter,
  BfMetricTileSize,
  BfMetricTileType,
  broadcastDataToBfMetricsDisplayModelMap,
  ColumnStrikeZoneDecisionTile,
  ColumnStrikeZoneDisplay,
  ColumnTile,
} from '@trackman/baseball-shared-components';
import { Paper } from '@trackman/web-shared-components';
import { ReactComponent as FilterIcon } from 'components/icons/filter.svg';
import { ServiceTypes } from 'constants/ServiceTypes';
import { IApiBallFinal } from 'interfaces/broadcast/IApiBallFinal';
import { StrikeZoneDefinitionEnum, StrikeZoneLocation } from 'interfaces/session/IStrikeZoneDefinition';
import { IStrikeZoneDecision } from 'interfaces/tagging/ApiModels/IStrikeZoneDecision';
import { observer } from 'mobx-react-lite';
import React, { useMemo } from 'react';
import { IFeatureConfigService } from 'services/FeatureConfigService';
import { convertDisplayUnits } from 'services/helper-services/convertDisplayUnits';
import { useInjection } from 'services/ioc/ioc.react';
import { MetricStore } from 'stores/MetricStore';
import { SectionHeadline } from '../SectionHeadline/SectionHeadline';
import { DisconnectedPlaceholder } from './DisconnectedPlaceholder/DisconnectedPlaceholder';
import { FilterIconWrapper, MetricsWrapper } from './MetricsStyles';

export type StrikeZoneDimension = Omit<IStrikeZoneDecision, 'strikeZoneType' | 'source' | 'strikeZoneDecisionLocations'>;

export interface IProps {
  ballFinals: IApiBallFinal[] | undefined;
  strikeZoneDecision?: IStrikeZoneDecision;
  connected: boolean;
  previousStrikeZoneDecision?: StrikeZoneDimension;
  isPlayPlaceholder?: boolean;
  strikeZoneType?: StrikeZoneDefinitionEnum;
}
export const Metrics: React.FC<IProps> = observer(
  ({ ballFinals, connected, strikeZoneDecision, previousStrikeZoneDecision, isPlayPlaceholder, strikeZoneType }) => {
    const metricStore = useInjection<MetricStore>(ServiceTypes.MetricStore);
    const { filteredMetrics, unitSystem, setIsSettingsMenuVisible, tileSize } = metricStore;
    const displayModel = useMemo(() => broadcastDataToBfMetricsDisplayModelMap(ballFinals || [], undefined, undefined), [ballFinals]);
    const featureConfigService = useInjection<IFeatureConfigService>(ServiceTypes.FeatureConfigService);

    const getPreviousStrikeZoneDecisionDimensions = () => {
      if (previousStrikeZoneDecision) {
        return {
          ...previousStrikeZoneDecision,
          isInZone: false,
          ballX: 0,
          ballY: 0,
          dimensions: {
            ...previousStrikeZoneDecision.dimensions,
            ballRadius: 0,
          },
        };
      }

      return undefined;
    };

    const getStrikeZoneDecision = (): StrikeZoneDimension | undefined => {
      if (!strikeZoneDecision) {
        if (strikeZoneType !== StrikeZoneDefinitionEnum.Static && isPlayPlaceholder) {
          return getPreviousStrikeZoneDecisionDimensions();
        }

        return undefined;
      }

      const priorityOrder = [StrikeZoneLocation.Front, StrikeZoneLocation.Middle, StrikeZoneLocation.Back];

      const data = strikeZoneDecision.strikeZoneDecisionLocations.find((location) =>
        priorityOrder.includes(location.strikeZoneLocationType)
      );

      return data ? { ...data, isInZone: strikeZoneDecision.isInZone } : { ...strikeZoneDecision, isInZone: strikeZoneDecision.isInZone };
    };

    const strikeZoneDecisionResult = useMemo(
      () => getStrikeZoneDecision(),
      [strikeZoneDecision, isPlayPlaceholder, previousStrikeZoneDecision]
    );

    const shouldRenderInOutIndicator = () => {
      return featureConfigService.getConfig().isRoboUmpEnabled && !isPlayPlaceholder;
    };

    return (
      <MetricsWrapper>
        <SectionHeadline>
          Metrics
          <FilterIconWrapper onClick={(): void => setIsSettingsMenuVisible(true)}>
            <FilterIcon />
          </FilterIconWrapper>
        </SectionHeadline>
        <Paper className='tagging-column'>
          {connected ? (
            <>
              {filteredMetrics
                .filter((x) => x.isChecked)
                .map((tile) => {
                  if (tile.type === BfMetricTileType.strikeZone) {
                    return (
                      <ColumnStrikeZoneDisplay
                        key={tile.id}
                        data={strikeZoneDecisionResult ? [strikeZoneDecisionResult] : undefined}
                        size={BfMetricTileSize.Small}
                        strikeZoneConfig={strikeZoneDecisionResult?.dimensions}
                        showInOutIndicator={shouldRenderInOutIndicator()}
                      />
                    );
                  }
                  if (tile.type === BfMetricTileType.strikeZoneDecision) {
                    return <ColumnStrikeZoneDecisionTile key={tile.type} isInZone={strikeZoneDecisionResult?.isInZone} size={tileSize} />;
                  }
                  return (
                    <ColumnTile
                      key={`bf-metric${tile.type}`}
                      data={bfMetricDataConverter(unitSystem, tile.type, displayModel[tile.type])}
                      units={convertDisplayUnits(unitSystem, tile.type, tileSize)}
                      title={tile.name}
                      type={tile.type}
                      size={tileSize}
                    />
                  );
                })}
            </>
          ) : (
            <DisconnectedPlaceholder />
          )}
        </Paper>
      </MetricsWrapper>
    );
  }
);
